// =============================================================================
// Self-service: registro, verificación, recuperación, tenant
// =============================================================================
import { apiClient } from './http';

export interface SignupRequest {
  company_name: string;
  /** NIF del empleador: el registro de jornada identifica a la empresa obligada (art. 34.9 ET) */
  cif: string;
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  /** honeypot: siempre vacío en humanos */
  website?: string;
}

export async function signup(data: SignupRequest): Promise<{ message: string }> {
  const res = await apiClient.post<{ message: string }>('/signup', data);
  return res.data;
}

export async function resendVerification(email: string): Promise<void> {
  await apiClient.post('/signup/resend', { email });
}

export async function verifyEmail(token: string): Promise<{ email: string }> {
  const res = await apiClient.get<{ email: string }>('/signup/verify', { params: { token } });
  return res.data;
}

export async function forgotPassword(email: string): Promise<void> {
  await apiClient.post('/auth/forgot-password', { email });
}

export async function resetPassword(data: { token: string; new_password: string }): Promise<void> {
  await apiClient.post('/auth/reset-password', data);
}

export interface InvitationInfo {
  first_name: string;
  company_name: string;
  // Sin correo, se activa con PIN (`activatePin`), no con contraseña
  // (`resetPassword`): son dos endpoints hermanos, y esto dice cuál tocaba.
  requires_pin: boolean;
}
export async function getInvitation(token: string): Promise<InvitationInfo> {
  const res = await apiClient.get<InvitationInfo>(`/auth/invitation/${token}`);
  return res.data;
}

export interface PinActivado {
  /** En claro y UNA sola vez: en la base sólo queda su hash. */
  pin: string;
  message: string;
}

/**
 * Activar una cuenta SIN correo — hermano de `resetPassword`.
 *
 * El PIN lo genera el SERVIDOR y lo devuelve: no se propone ninguno. Que lo
 * eligiera el trabajador obligaba a decirle cuándo chocaba con el de otra
 * persona, y ese mensaje convertía una pantalla pública en un listado de qué
 * PIN valen en esa empresa.
 */
export async function activatePin(data: { token: string }): Promise<PinActivado> {
  const res = await apiClient.post<PinActivado>('/auth/activate-pin', data);
  return res.data;
}

export interface TenantStatus {
  name: string;
  plan: string;
  trial_ends_at: string | null;
  trial_days_left: number | null;
  country?: string;
  /** Puede faltar en empresas dadas de alta antes de que fuera obligatorio */
  cif?: string | null;
  address?: string | null;
  jurisdiction?: { code: string; name: string; national?: boolean; national_pending?: boolean };
}

export async function getTenantStatus(): Promise<TenantStatus> {
  const res = await apiClient.get<TenantStatus>('/tenant');
  return res.data;
}

export interface TenantPatch {
  name?: string;
  cif?: string;
  address?: string;
  country?: string;
}

export async function updateTenant(patch: TenantPatch): Promise<TenantStatus> {
  const res = await apiClient.patch<TenantStatus>('/tenant', patch);
  return res.data;
}

/** Lo que devuelve `GET /auth/me` (backend: `UserResponse`). */
export interface CurrentUser {
  id: number;
  // Nullable, como `AdminUser.email`: desde `/auth/recuperar-acceso` el que
  // llama a `/auth/me` es justamente un trabajador SIN correo, y el backend
  // devuelve `null`. Estaba declarado `string` y `tsc` pasaba limpio porque
  // el tipo mentía, no porque el dato fuera una cadena.
  email: string | null;
  first_name: string;
  last_name: string;
  full_name: string;
  employee_code: string | null;
  dni: string | null;
  role: string;
  is_active: boolean;
  tenant_id: number;
  work_policy_id: number | null;
  shift_template_id: number | null;
  created_at: string | null;
}

export async function getCurrentUser(): Promise<CurrentUser> {
  const response = await apiClient.get<CurrentUser>('/auth/me');
  return response.data;
}
